import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { FeedbackCounts } from '../api/feedbackApi';

const grow = {
  transformOrigin: 'left',
  '@keyframes shareGrow': { from: { transform: 'scaleX(0)' }, to: { transform: 'scaleX(1)' } },
  animation: 'shareGrow 700ms cubic-bezier(0.2, 0, 0, 1) both',
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
} as const;

function Legend({ color, children }: { color: string; children: React.ReactNode }) {
  return (
    <Stack direction="row" sx={{ alignItems: 'center', gap: 0.75 }}>
      <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: color }} aria-hidden />
      <Typography variant="caption">{children}</Typography>
    </Stack>
  );
}

/** The share of rated answers RMs found helpful, as a number and a split bar. */
export function FeedbackSummary({ counts }: { counts: FeedbackCounts }) {
  const total = counts.up + counts.down;
  const share = total ? Math.round((counts.up / total) * 100) : 0;
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} sx={cardSx}>
      <Box sx={{ flexShrink: 0 }}>
        <Typography sx={{ fontSize: '2.25rem', fontWeight: 600, lineHeight: 1 }}>{total ? `${share}%` : '—'}</Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>rated helpful</Typography>
      </Box>
      <Stack sx={{ flexGrow: 1, gap: 1, width: '100%' }}>
        <Stack direction="row" sx={{ height: 6, gap: '3px' }} aria-hidden>
          {counts.up ? <Box sx={{ ...grow, flexGrow: counts.up, bgcolor: 'market.up', borderRadius: 3 }} /> : null}
          {counts.down ? <Box sx={{ ...grow, flexGrow: counts.down, bgcolor: 'market.down', borderRadius: 3, animationDelay: '150ms' }} /> : null}
          {total ? null : <Box sx={{ flexGrow: 1, bgcolor: 'surface.sunken', borderRadius: 3 }} />}
        </Stack>
        <Stack direction="row" sx={{ alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Legend color="market.up"><strong>{counts.up}</strong> helpful</Legend>
          <Legend color="market.down"><strong>{counts.down}</strong> reported</Legend>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>All ratings · Portal and Teams</Typography>
        </Stack>
      </Stack>
    </Stack>
  );
}

const cardSx = {
  alignItems: { sm: 'center' },
  gap: { xs: 2, sm: 4 },
  p: { xs: 2, md: 3 },
  bgcolor: 'background.paper',
  border: 1,
  borderColor: 'divider',
  borderRadius: 1.5,
} as const;
